import { useEffect, useState, type ReactNode } from 'react'
import { ChevronDown, LogOut, Menu, X } from 'lucide-react'
import { NavLink, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils.ts'
import { MegaMenu, type NavCategory, type NavItem } from './MegaMenu.tsx'

export type { NavCategory, NavItem }

export interface AppShellBrand {
  /** Route zum "Home" hinter Logo-Klick */
  homeTo: string
  /** Haupt-Wortmarke (z. B. "implec") */
  title: string
  /** Unter-Zeile (z. B. "Systemhaus-Manager" oder "Kundenportal") */
  subtitle: string
  /** Hintergrund-Klasse fuer das Logo-Quadrat (default: bg-accent-500) */
  logoBgClass?: string
  /** Zeichen im Logo-Quadrat (default: "i") */
  logoText?: string
}

export interface AppShellProps {
  brand: AppShellBrand
  /** Navigations-Kategorien (bereits gefiltert durch den Aufrufer) */
  nav: NavCategory[]
  /** Utility-Items fuer das mobile Menue (optional) */
  utilityItems?: NavItem[]
  /** Angemeldeter User fuer Anzeige + Mobile-Panel-Header */
  user: { name?: string; email?: string } | null
  onLogout: () => void
  /** Zusaetzliche Controls rechts im Desktop-Header (TenantSelector, Clock, Demo-Mode, …) */
  headerExtras?: ReactNode
  /** Optionale Top-Leiste ueber dem Header (Dev-Mode-Banner, Version-Strip, GlobalStatusBar) */
  topBar?: ReactNode
  /** Optionale Kontext-Leiste zwischen Header und Content (Mandanten-Selektor) */
  contextBar?: ReactNode
  children: ReactNode
}

export function AppShell({
  brand,
  nav,
  utilityItems,
  user,
  onLogout,
  headerExtras,
  topBar,
  contextBar,
  children,
}: AppShellProps) {
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [mobileExpandedCategory, setMobileExpandedCategory] = useState<string | null>(null)

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false)
    setMobileExpandedCategory(null)
  }, [location.pathname])

  const logoBgClass = brand.logoBgClass ?? 'bg-accent-500'
  const logoText = brand.logoText ?? 'i'

  return (
    <div className="flex flex-col h-screen bg-gray-50 overflow-hidden">
      {topBar}

      <header className="bg-brand-700 text-white px-4 lg:px-6 flex items-center justify-between h-14 flex-shrink-0 relative z-50">
        {/* Left: Logo + Navigation */}
        <div className="flex items-center gap-4">
          <NavLink to={brand.homeTo} className="flex items-center gap-2.5 mr-2">
            <div className={cn('w-7 h-7 rounded flex items-center justify-center', logoBgClass)}>
              <span className="text-white font-bold text-xs leading-none">{logoText}</span>
            </div>
            <div className="hidden sm:block">
              <p className="text-sm font-bold leading-none tracking-tight">{brand.title}</p>
              <p className="text-[10px] text-white/50 leading-none mt-0.5">{brand.subtitle}</p>
            </div>
          </NavLink>

          <MegaMenu categories={nav} />
        </div>

        {/* Right: Extras + User */}
        <div className="flex items-center gap-3">
          {headerExtras}

          {user?.name && (
            <span className="hidden lg:inline text-sm text-white/70">{user.name}</span>
          )}

          <button
            onClick={onLogout}
            className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded text-sm text-white/70 hover:bg-white/10 hover:text-white transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span className="hidden lg:inline">Abmelden</span>
          </button>

          <button
            onClick={() => setMobileOpen((o) => !o)}
            className="xl:hidden p-1.5 rounded hover:bg-white/10 text-white/70 hover:text-white transition-colors"
            aria-label={mobileOpen ? 'Menü schließen' : 'Menü öffnen'}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {contextBar}

      {mobileOpen && (
        <div className="fixed inset-0 top-14 z-40 xl:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMobileOpen(false)} />
          <div className="relative bg-white w-full max-w-sm h-full overflow-y-auto shadow-xl">
            {user && (
              <div className="px-4 py-3 bg-brand-50 border-b border-brand-100">
                {user.name && <p className="text-sm font-semibold text-brand-700">{user.name}</p>}
                {user.email && <p className="text-xs text-brand-500">{user.email}</p>}
              </div>
            )}

            <nav className="py-2">
              {nav.map((category) => (
                <div key={category.id}>
                  <button
                    onClick={() =>
                      setMobileExpandedCategory(
                        mobileExpandedCategory === category.id ? null : category.id,
                      )
                    }
                    className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    {category.label}
                    <ChevronDown
                      className={cn(
                        'w-4 h-4 text-gray-400 transition-transform',
                        mobileExpandedCategory === category.id && 'rotate-180',
                      )}
                    />
                  </button>

                  {mobileExpandedCategory === category.id && (
                    <div className="pb-1">
                      {category.items.map((item) => {
                        const Icon = item.icon
                        return (
                          <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.end}
                            className={({ isActive }) =>
                              cn(
                                'flex items-center gap-3 px-6 py-2 text-sm transition-colors',
                                isActive
                                  ? 'bg-brand-50 text-brand-700 font-medium'
                                  : 'text-gray-600 hover:bg-gray-50',
                              )
                            }
                          >
                            <Icon className="w-4 h-4 flex-shrink-0" />
                            {item.label}
                          </NavLink>
                        )
                      })}
                    </div>
                  )}
                </div>
              ))}

              {utilityItems && utilityItems.length > 0 && (
                <div className="border-t border-gray-200 mt-2 pt-2">
                  {utilityItems.map((item) => {
                    const Icon = item.icon
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        className={({ isActive }) =>
                          cn(
                            'flex items-center gap-3 px-4 py-2.5 text-sm transition-colors',
                            isActive
                              ? 'bg-brand-50 text-brand-700 font-medium'
                              : 'text-gray-600 hover:bg-gray-50',
                          )
                        }
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        {item.label}
                      </NavLink>
                    )
                  })}
                </div>
              )}

              <div className="border-t border-gray-200 mt-2 pt-2 sm:hidden">
                <button
                  onClick={onLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  Abmelden
                </button>
              </div>
            </nav>
          </div>
        </div>
      )}

      <main className="flex-1 overflow-y-auto p-6">{children}</main>
    </div>
  )
}
